import React, { useEffect, useState } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Drawer, Flex, Popover, Spin, Table, Tooltip } from "antd";
import { Badge, Button, Spinner } from "reactstrap";
import dayjs from "dayjs";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import { useDispatch, useSelector } from "react-redux";
import { setUploadHighlight } from "../../../store/slices/misc/highlightSlice";
import { ReactTyped } from "react-typed";
import { Icon } from "../../../components/Component";
import Swal from "sweetalert2";
import { uploadApis } from "../../../api/Uploads";
import {
  wbsUploadPreviewColumns,
  WBS_UPLOAD_TABLE_WIDTH,
} from "../../../components/upload/shared/previewColumns/wbsUploadPreviewColumns";
import {
  taskBoqUploadPreviewColumns,
  TASK_BOQ_UPLOAD_TABLE_WIDTH,
  taskBudgetUploadPreviewColumns,
  TASK_BUDGET_UPLOAD_TABLE_WIDTH,
  taskResourceUploadPreviewColumns,
  TASK_RESOURCE_UPLOAD_TABLE_WIDTH,
  taskToolUploadPreviewColumns,
  TASK_TOOL_UPLOAD_TABLE_WIDTH,
} from "../../../components/upload/shared/previewColumns/taskUploadPreviewColumns";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { useLocation, useParams } from "react-router";

const drawerColConfig: any = {
  // Columns extracted to the shared upload primitives so the staged upload
  // modals and this History drawer render the exact same preview.
  "wbs-upload": {
    title: "WBS Upload",
    columns: wbsUploadPreviewColumns,
    tableWidth: WBS_UPLOAD_TABLE_WIDTH,
  },
  "task-boq-upload": {
    title: "Task BOQ",
    columns: taskBoqUploadPreviewColumns,
    tableWidth: TASK_BOQ_UPLOAD_TABLE_WIDTH,
  },
  "norms-upload": {
    title: "Norms Master",
    columns: [
      {
        title: "Norm Code",
        dataIndex: "normCode",
        key: "normCode",
        width: 110,
        fixed: "left",
      },
      {
        title: "Norm Title",
        dataIndex: "normTitle",
        key: "normTitle",
        width: 220,
      },
      {
        title: "Material Name",
        dataIndex: "materialName",
        key: "materialName",
        width: 200,
      },
      {
        title: "Material Match",
        dataIndex: "materialStatus",
        key: "materialStatus",
        width: 130,
      },
      {
        title: "Consumption Norm/Unit",
        dataIndex: "consumptionNorm",
        key: "consumptionNorm",
        width: 170,
      },
      {
        title: "Wastage (%)",
        dataIndex: "wastagePercent",
        key: "wastagePercent",
        width: 110,
      },
      {
        title: "Unit Price (₹)",
        dataIndex: "unitPrice",
        key: "unitPrice",
        width: 120,
      },
      {
        title: "PUC Factor",
        dataIndex: "pucFactor",
        key: "pucFactor",
        width: 100,
      },
    ],
    tableWidth: 1200,
  },
  "task-resource-upload": {
    title: "Task Resources",
    columns: taskResourceUploadPreviewColumns,
    tableWidth: TASK_RESOURCE_UPLOAD_TABLE_WIDTH,
  },
  "task-tool-upload": {
    title: "Task Tools & Machinery",
    columns: taskToolUploadPreviewColumns,
    tableWidth: TASK_TOOL_UPLOAD_TABLE_WIDTH,
  },
  "task-budget-upload": {
    title: "Task Budget",
    columns: taskBudgetUploadPreviewColumns,
    tableWidth: TASK_BUDGET_UPLOAD_TABLE_WIDTH,
  },
};

const BulkUploadList = () => {
  const [screenSize, setScreenSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [activeReportId, setActiveReportId] = useState(null);
  const [activeReportType, setActiveReportType] = useState(null);
  const [pagination, setPagination] = useState<{
    pageSize: number;
    currentPage: number;
    totalPageCount: number;
    totalRowCount: number;
  }>({
    pageSize: 10,
    currentPage: 1,
    totalPageCount: 0,
    totalRowCount: 0,
  });
  const [drawerData, setDrawerData] = useState<{
    columns: any[];
    dataSource: any[];
    tableWidth: number;
  }>({
    columns: [],
    dataSource: [],
    tableWidth: 1000,
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const { projectName, id: stateProjectId } = location.state || {};
  const projectId = urlProjectId || stateProjectId;

  const [drawerVisible, setDrawerVisible] = useState(false);
  const uploadHighlighted = useSelector(
    (state: any) => state.highlight.uploadHighlighted
  );

  const handleDeleteReport = async (reportId: any) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        setDeleting(reportId);
        const res = await uploadApis.deleteExcelCreationById(reportId);
        const { status, message } = res || {};

        if (status) {
          setRefreshToggle((pre) => !pre);
          Swal.fire(
            "Deleted!",
            message || "Report deleted successfully",
            "success"
          );
        } else {
          Swal.fire("Error", message || "Failed to delete report", "error");
        }
      }
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire(
        "Error",
        "Something went wrong while deleting the report",
        "error"
      );
    } finally {
      setDeleting(null);
    }
  };

  const handleCreation = async (reportId: any) => {
    try {
      setSubmitting(reportId);
      const res = await uploadApis.processExcelCreationById(reportId);
      const { status, message } = res || {};

      if (status) {
        setRefreshToggle((pre) => !pre);
        Swal.fire(
          "Created",
          message || "Report processed successfully",
          "success"
        );
      } else {
        Swal.fire("Error", message || "Failed to process report", "error");
      }
    } catch (error) {
      console.error("Creation error:", error);
      Swal.fire(
        "Error",
        "Something went wrong while processing the report",
        "error"
      );
    } finally {
      setSubmitting(null);
    }
  };

  const fetchFileContent = async (
    reportId: any,
    currentPage: number = pagination.currentPage,
    pageSize: number = pagination.pageSize
  ) => {
    try {
      setLoading(true);

      const res = await uploadApis.fetchWBSReportById(
        reportId,
        currentPage,
        pageSize
      );
      const { status, data } = res || {};

      if (status && data) {
        const list = data?.convertedJson || [];
        const p = data?.pagination || {};

        setDrawerData((prev) => ({
          ...prev,
          dataSource: list,
        }));

        setPagination({
          pageSize: p.pageSize,
          currentPage: p.currentPage,
          totalPageCount: p.totalPages,
          totalRowCount: p.totalRecords,
        });
      }
    } catch (error) {
      console.error("Error fetching file content:", error);
    } finally {
      setLoading(false);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  useEffect(() => {
    if (uploadHighlighted) {
      dispatch(setUploadHighlight(false));
    }
  }, [uploadHighlighted, dispatch]);

  const getDrawerTitle = (reportType: string | null) => {
    const config = reportType ? drawerColConfig[reportType] : null;
    if (config) {
      return `${config.title} - File Data Preview`;
    }
    return `${reportType || "WBS Upload"} - File Data Preview`;
  };

  const columns: any = [
    {
      title: "Report Id",
      dataIndex: "reportId",
      key: "reportId",
      ellipsis: true,
      width: 70,
    },
    {
      title: "Report Type",
      dataIndex: "reportType",
      key: "reportType",
      ellipsis: true,
      width: 120,
      render: (text: any) => <span>{text || "WBS Upload"}</span>,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      ellipsis: true,
      width: 120,
      render: (text: any) => (
        <Tooltip title={text}>
          <span>
            {text?.length > 50 ? `${text.substring(0, 30)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "requestedDate",
      key: "requestedDate",
      ellipsis: true,
      width: 120,
      render: (text: any) => (
        <span>{dayjs(text).format("DD-MMM-YYYY HH:mm")}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      ellipsis: true,
      width: 130,
      render: (text: any, record: any) => {
        if (
          record?.uploadStatus === "Submit" &&
          record?.status === "Processed"
        ) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircleOutlined style={{ color: "green" }} />
              <span style={{ color: "green", fontSize: "14px" }}>
                Created Successfully
              </span>
            </div>
          );
        }

        const status = record?.status;
        switch (status) {
          case "Processing":
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <SyncOutlined spin style={{ color: "orange" }} />
                <span style={{ color: "orange", fontSize: "14px" }}>
                  Processing
                  <ReactTyped
                    strings={["...."]}
                    typeSpeed={50}
                    backSpeed={10}
                    loop
                    showCursor={false}
                  />
                </span>
              </div>
            );
          case "Failed":
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CloseCircleOutlined style={{ color: "red" }} />
                <span style={{ color: "red", fontSize: "14px" }}>Failed</span>
              </div>
            );
          case "Error":
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CloseCircleOutlined style={{ color: "red" }} />
                <span style={{ color: "red", fontSize: "14px" }}>
                  Error In File
                </span>
              </div>
            );
          case "Processed":
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CheckCircleOutlined style={{ color: "green" }} />
                <span style={{ color: "green", fontSize: "14px" }}>
                  Processed Successfully
                </span>
              </div>
            );
          case "Requested":
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <SyncOutlined style={{ color: "blue" }} />
                <span style={{ color: "blue", fontSize: "14px" }}>
                  Requested
                </span>
              </div>
            );
          case "Validating":
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <SyncOutlined spin style={{ color: "gold" }} />
                <span style={{ color: "#FFB800", fontSize: "14px" }}>
                  Validating
                  <ReactTyped
                    strings={["...."]}
                    typeSpeed={50}
                    backSpeed={10}
                    loop
                    showCursor={false}
                  />
                </span>
              </div>
            );
          default:
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "14px" }}>{record?.status}</span>
              </div>
            );
        }
      },
    },
    {
      title: "Action",
      key: "action",
      ellipsis: true,
      width: 100,
      render: (text: any, record: any) => {
        // console.log(record, "record");

        // Don't show delete icon for Processing status
        const showDelete = record?.status !== "Processing";

        const downloadIcon = record.filePath && (
          <Tooltip title="Download Uploaded File">
            <span
              onClick={(e) => {
                e.stopPropagation();
                downloadDocument(
                  record.filePath,
                  `report-${record.reportId}.xlsx`
                );
              }}
              style={{
                color: "#1890ff",
                cursor: "pointer",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon name="download" />
            </span>
          </Tooltip>
        );

        if (record?.uploadStatus == "Submit" && record?.status == "Processed")
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span
                className="inline-link"
                id={`SuccessStatus-${record?.reportId}`}
                onClick={() => {
                  setDrawerData((pre: any) => {
                    const _config = drawerColConfig[record?.reportType] || {};
                    return {
                      ...pre,
                      columns: _config?.columns,
                      tableWidth: _config?.tableWidth,
                    };
                  });
                  setActiveReportId(record?.reportId);
                  setActiveReportType(record?.reportType);
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                  setDrawerVisible(true);
                  fetchFileContent(record?.reportId);
                }}
              >
                {/* View */}
                <Icon
                  name="eye"
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
              </span>

              {/* download icon */}
              {downloadIcon}

              {/* DELETE ICON - Only show if not Processing */}
              {showDelete && (
                <>
                  {deleting === record?.reportId ? (
                    <Spinner size="sm" color="danger" />
                  ) : (
                    <Icon
                      name="trash"
                      style={{
                        color: "#ff4d4f",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                      onClick={() => handleDeleteReport(record?.reportId)}
                    />
                  )}
                </>
              )}
            </div>
          );
        const status = record?.status;
        switch (status) {
          case "Failed":
            return (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Popover
                  content={
                    <div>
                      <p>{record?.error}</p>
                    </div>
                  }
                  placement="right"
                  title={<span style={{ color: "red" }}>Failed</span>}
                >
                  <Badge color="outline-danger" style={{ cursor: "pointer" }}>
                    Reason
                  </Badge>
                </Popover>

                {/* download icon */}
                {downloadIcon}

                {/* DELETE ICON - Only show if not Processing */}
                {showDelete && (
                  <>
                    {deleting === record?.reportId ? (
                      <Spinner size="sm" color="danger" />
                    ) : (
                      <Icon
                        name="trash"
                        style={{
                          color: "#ff4d4f",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                        onClick={() => handleDeleteReport(record?.reportId)}
                      />
                    )}
                  </>
                )}
              </div>
            );
          case "Error":
            return (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    textDecoration: "none",
                  }}
                  className="inlineTableCellLink"
                  id={`ErrorStatus-${record?.reportId}`}
                  onClick={() => {
                    setDrawerData((pre: any) => {
                      const _config = drawerColConfig[record?.reportType] || {};
                      return {
                        ...pre,
                        columns: _config?.columns,
                        tableWidth: _config?.tableWidth,
                      };
                    });
                    setActiveReportId(record?.reportId);
                    setActiveReportType(record?.reportType);
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                    setDrawerVisible(true);
                    fetchFileContent(record?.reportId);
                  }}
                >
                  {/* View */}
                  <Icon
                    name="eye"
                    style={{ fontSize: "16px", color: "#1890ff" }}
                  />
                </span>

                {/* download icon */}
                {downloadIcon}

                {/* DELETE ICON - Only show if not Processing */}
                {showDelete && (
                  <>
                    {deleting === record?.reportId ? (
                      <Spinner size="sm" color="danger" />
                    ) : (
                      <Icon
                        name="trash"
                        style={{
                          color: "#ff4d4f",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                        onClick={() => handleDeleteReport(record?.reportId)}
                      />
                    )}
                  </>
                )}
              </div>
            );
          case "Processed":
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    textDecoration: "none",
                  }}
                  className="inlineTableCellLink"
                  id={`ProcessedViewStatus-${record?.reportId}`}
                  onClick={() => {
                    setDrawerData((pre: any) => {
                      const _config = drawerColConfig[record?.reportType] || {};
                      return {
                        ...pre,
                        columns: _config?.columns,
                        tableWidth: _config?.tableWidth,
                      };
                    });
                    setActiveReportId(record?.reportId);
                    setActiveReportType(record?.reportType);
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                    setDrawerVisible(true);
                    fetchFileContent(record?.reportId);
                  }}
                >
                  {/* View */}
                  <Icon
                    name="eye"
                    style={{ fontSize: "16px", color: "#1890ff" }}
                  />
                </span>

                <span id={`createBtn-${record?.reportId}`}>
                  {submitting == record?.reportId ? (
                    <Button size="sm" color="primary">
                      <Spinner size="sm" color="light" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      color="primary"
                      disabled={!!submitting}
                      onClick={() => {
                        setSubmitting(record?.reportId);
                        handleCreation(record?.reportId);
                      }}
                    >
                      Create
                    </Button>
                  )}
                </span>

                {/* download icon */}
                {downloadIcon}

                {/* DELETE ICON - Only show if not Processing */}
                {showDelete && (
                  <>
                    {deleting === record?.reportId ? (
                      <Spinner size="sm" color="danger" />
                    ) : (
                      <Icon
                        name="trash"
                        style={{
                          color: "#ff4d4f",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                        onClick={() => handleDeleteReport(record?.reportId)}
                      />
                    )}
                  </>
                )}
              </div>
            );
          case "Processing":
            return (
              // Only show processing status without delete icon
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* <SyncOutlined spin style={{ color: "orange" }} />
                <span style={{ color: "orange", fontSize: "14px" }}>
                  Processing
                  <ReactTyped
                    strings={["...."]}
                    typeSpeed={50}
                    backSpeed={10}
                    loop
                    showCursor={false}
                  />
                </span> */}

                {/* download icon */}
                {downloadIcon}
              </div>
            );
          default:
            // For other statuses (Requested, Validating, etc.), show delete icon if not Processing
            return showDelete ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                {/* download icon */}
                {downloadIcon}

                {deleting === record?.reportId ? (
                  <Spinner size="sm" color="danger" />
                ) : (
                  <Icon
                    name="trash"
                    style={{
                      color: "#ff4d4f",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                    onClick={() => handleDeleteReport(record?.reportId)}
                  />
                )}
              </div>
            ) : null;
        }
      },
    },
  ];

  // Function to download document
  const downloadDocument = (filePath: string, fileName: string) => {
    try {
      if (!filePath) {
        Swal.fire("Error", "File path not found", "error");
        return;
      }

      const isAbsoluteUrl = /^https?:\/\//i.test(filePath);
      const fullUrl = isAbsoluteUrl
        ? filePath
        : `${window.location.origin}/${filePath.replace(/^\//, "")}`;

      const link = document.createElement("a");
      link.href = fullUrl;
      link.download = fileName || "report.xlsx";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      Swal.fire(
        "Error",
        "Something went wrong while downloading the file",
        "error"
      );
    }
  };

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [location]);

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="bulk-upload-list"
        title={projectName ? `${projectName} / Bulk Upload Requested List` : "Bulk Upload Requested List"}
        projectId={projectId}
        refresh={refreshToggle}
        columns={columns}
        // extra={{
        //   tableScrollX: 1500,
        // }}
        extra={{
          tableScrollX: Math.min(900, window.innerWidth - 100),
        }}
      />
      <Drawer
        title={getDrawerTitle(activeReportType)}
        placement="right"
        width={"90%"}
        open={drawerVisible}
        onClose={() => {
          setDrawerData(() => {
            return {
              columns: [],
              dataSource: [],
              tableWidth: 1000,
            };
          });
          setPagination(() => {
            return {
              currentPage: 1,
              totalPageCount: 0,
              totalRowCount: 0,
              pageSize: 10,
            };
          });
          setActiveReportId(null);
          setDrawerVisible(false);
        }}
      >
        {loading ? (
          <Flex align="center" justify="center" style={{ height: 200 }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <div className="table-responsive">
            <Table
              rowKey={(record) => record.taskId || Math.random().toString()}
              columns={drawerData?.columns}
              dataSource={drawerData?.dataSource}
              scroll={{ x: drawerData?.tableWidth }}
              bordered
              // pagination={false}
              pagination={{
                current: pagination.currentPage,
                pageSize: pagination.pageSize,
                total: pagination.totalRowCount,
                showSizeChanger: true,
                // showQuickJumper: false,
                onChange: (page, pageSize) => {
                  fetchFileContent(activeReportId, page, pageSize);
                },
              }}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default BulkUploadList;
